/**
 * Background job contracts — DESIGN.md §4, §11, §16.5, §16.6.
 *
 * The API enqueues and the worker consumes, so these payloads cross a process
 * boundary and belong in the shared package (§18.4 clause 2). Queue names are
 * exactly the five in §4; a new kind of work becomes a new *job name* on an
 * existing queue unless it needs its own concurrency or retry policy.
 */
import { z } from 'zod';
export const QUEUE_NAMES = [
    'email-instant',
    'email-digest',
    'thumbnails',
    'automations',
    'maintenance',
];
/**
 * `email-instant` carries two job kinds: notification emails (§11.2) and
 * transactional mail such as invites and backup alerts. Both belong here
 * because §11.4 is absolute — "the API never sends mail inline" — and both
 * want the same retry policy.
 */
export const instantEmailJobSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('notifications'),
        userId: z.string().uuid(),
        notificationIds: z.array(z.number().int().positive()).min(1),
    }),
    z.object({
        kind: z.literal('invite'),
        userId: z.string().uuid(),
        /** Signed invite token — §9.1. Short-lived, so it is safe to queue. */
        token: z.string().min(10),
        invitedByName: z.string(),
    }),
    z.object({
        kind: z.literal('admin-alert'),
        subject: z.string().min(1),
        body: z.string().min(1),
    }),
    z.object({
        /** A person checking that mail reaches them — Settings → Email. */
        kind: z.literal('test'),
        userId: z.string().uuid(),
        testId: z.string().uuid(),
    }),
]);
/**
 * The digest sweep — §11.2. Runs every 5 minutes as a repeatable job and
 * decides for itself which users are due; it carries no per-user payload so a
 * missed run costs nothing but latency.
 */
export const digestJobSchema = z.object({
    kind: z.literal('sweep'),
    /** Set by tests to pin "now"; production always uses the wall clock. */
    now: z.string().datetime().optional(),
});
export const thumbnailJobSchema = z.object({
    attachmentId: z.string().uuid(),
});
export const automationJobSchema = z.object({
    eventId: z.number().int().positive(),
    /** Loop guard — §14.3: an automation-caused event stops re-triggering at 3. */
    depth: z.number().int().min(0).max(3).default(0),
});
export const maintenanceJobSchema = z.object({
    task: z.enum([
        'backup',
        'restore-check',
        'purge-soft-deleted',
        'prune-notifications',
        'thumbnail-backfill',
        'disk-space-check',
        'queue-reconcile',
        'due-soon-sweep',
        'recurrence-spawn',
        'position-rebalance',
        // #99 — fire reminders that have come due. Every minute.
        'reminders',
    ]),
    /** Job-specific arguments; validated by each handler. */
    args: z.record(z.unknown()).optional(),
});
//# sourceMappingURL=jobs.js.map